import type { Translation } from '@vlm/i18n';
import type { AdminMessages } from './en';

/**
 * VLM Admin — العربية. Written, not translated (brand/BRAND.md §5): the glossary's words — مركبة،
 * رحلة، سياج جغرافي، جهاز تتبع، تنبيه، متحركة، غير متصلة — and Western digits. Wording that
 * the old admin's ar.json already had is kept where it fits the glossary. To be read once by a
 * native-speaking reviewer before launch.
 */
export const ar: Translation<AdminMessages> = {
  app: {
    name: 'إدارة VLM',
    skip: 'انتقل إلى المحتوى',
    menu: 'القائمة',
    navigation: 'التنقل الرئيسي',
  },

  common: {
    save: 'حفظ',
    saveChanges: 'حفظ التغييرات',
    cancel: 'إلغاء',
    close: 'إغلاق',
    confirm: 'تأكيد',
    edit: 'تعديل',
    delete: 'حذف',
    back: 'رجوع',
    add: 'إضافة',
    search: 'بحث',
    clearSearch: 'مسح البحث',
    retry: 'حاول مرة أخرى',
    loading: 'جارٍ التحميل',
    refresh: 'تحديث',
    dismiss: 'إخفاء',
    actions: 'الإجراءات',
    status: 'الحالة',
    all: 'الكل',
    anyStatus: 'كل الحالات',
    notes: 'ملاحظات',
    notesPlaceholder: 'أي شيء يستحق المعرفة',
    optional: 'اختياري',
    name: 'الاسم',
    type: 'النوع',
    from: 'من',
    to: 'إلى',
    period: 'الفترة',
    view: 'عرض',
    more: 'المزيد',
    never: 'أبدًا',
    none: 'لا شيء',
    showOnMap: 'عرض على الخريطة',
    required: 'مطلوب',
    tooLong: '{max} حرفًا على الأكثر',
    invalidEmail: 'أدخل بريدًا إلكترونيًا مثل name@company.com',
    invalidNumber: 'أدخل رقمًا',
    mustBePositive: 'يجب أن تكون القيمة أكبر من 0',
    yearRange: 'سنة بين 1950 و2100',
    range: '{from}–{to} من {total}',
    previousPage: 'الصفحة السابقة',
    nextPage: 'الصفحة التالية',
    rowsPerPage: 'صفوف في الصفحة',
    noMatchTitle: 'لا نتائج مطابقة',
    noMatchBody: 'جرّب بحثًا آخر، أو امسح عوامل التصفية.',
    clearFilters: 'مسح عوامل التصفية',
    loadFailedTitle: 'تعذّر التحميل',
    loadFailedBody: 'تحقّق من الاتصال وحاول مرة أخرى.',
    vehicle: 'المركبة',
    allVehicles: 'كل المركبات',
    chooseVehicle: 'اختر مركبة',
    searchVehicles: 'ابحث عن مركبة',
    noVehicleMatch: 'لا توجد مركبة مطابقة',
    selected: 'محدد',
    activate: 'تفعيل',
    deactivate: 'تعطيل',
  },

  units: {
    km: 'كم',
    kmh: 'كم/س',
  },

  presets: {
    today: 'اليوم',
    yesterday: 'أمس',
    last7: 'آخر 7 أيام',
    last30: 'آخر 30 يومًا',
    thisMonth: 'هذا الشهر',
    custom: 'مخصص',
  },

  nav: {
    live: 'الخريطة المباشرة',
    vehicles: 'المركبات',
    devices: 'أجهزة التتبع',
    assignments: 'الربط',
    trips: 'الرحلات',
    geofences: 'الأسيجة الجغرافية',
    alerts: 'التنبيهات',
    reports: 'التقارير',
    users: 'المستخدمون',
    alertSettings: 'إعدادات التنبيهات',
    billing: 'الفوترة',
    profile: 'ملفك الشخصي',
  },

  shell: {
    theme: 'المظهر',
    themeDay: 'نهاري',
    themeNight: 'ليلي',
    themeAuto: 'حسب النظام',
    language: 'اللغة',
    switchLanguage: 'English',
    switchLanguageLabel: 'التبديل إلى الإنجليزية',
    signOut: 'تسجيل الخروج',
    account: 'الحساب',
    liveOn: 'مباشر',
    liveConnecting: 'جارٍ الاتصال',
    liveReconnecting: 'جارٍ إعادة الاتصال',
    liveOff: 'غير مباشر',
    liveOnHint: 'تصل المواقع والتنبيهات فور إرسال المركبات لها.',
    liveDownHint: 'انقطع البث المباشر وتجري إعادة الاتصال. تتحدّث الخريطة كل 30 ثانية حتى يعود.',
    paymentOverdue: 'دفعة متأخرة',
    paymentFirst: 'بانتظار الدفع',
    paymentOverdueHint: 'كان موعد دفع {amount} في {date}. تفاصيل الحساب البنكي في صفحة الفوترة.',
    paymentFirstHint: 'تبدأ باقتك بدفعتها الأولى، {amount}. تفاصيل الحساب البنكي في صفحة الفوترة.',
    trial: 'تجربة مجانية',
    trialChip: {
      zero: 'تجربة • انتهت',
      one: 'تجربة • آخر يوم',
      two: 'تجربة • بقي يومان',
      few: 'تجربة • بقيت {count} أيام',
      many: 'تجربة • بقي {count} يومًا',
      other: 'تجربة • بقي {count} يوم',
    },
    trialHint: 'كل شيء بلا حدود حتى نهاية يوم {date}. اختر باقة مع فيجانيوم قبل ذلك لتواصل تسجيل الدخول.',
    trialHintPlan: 'كل شيء بلا حدود حتى نهاية يوم {date}، وتبدأ باقتك في اليوم التالي.',
    expandMenu: 'توسيع القائمة',
    collapseMenu: 'طيّ القائمة',
  },

  notifications: {
    title: 'الإشعارات',
    open: 'الإشعارات',
    unread: {
      zero: 'لا جديد',
      one: 'واحد غير مقروء',
      two: 'اثنان غير مقروءين',
      few: '{count} غير مقروءة',
      many: '{count} غير مقروء',
      other: '{count} غير مقروء',
    },
    unreadLabel: {
      zero: 'لا إشعارات غير مقروءة',
      one: 'إشعار واحد غير مقروء',
      two: 'إشعاران غير مقروءين',
      few: '{count} إشعارات غير مقروءة',
      many: '{count} إشعارًا غير مقروء',
      other: '{count} إشعار غير مقروء',
    },
    empty: 'لا توجد إشعارات',
    emptyBody: 'تصل تنبيهات أسطولك إلى هنا لحظة وقوعها.',
    markAllRead: 'تحديد الكل كمقروء',
    viewAlerts: 'كل التنبيهات',
  },

  auth: {
    title: 'تسجيل الدخول',
    subtitle: 'إدارة VLM • Viganium Fleet Management',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    showPassword: 'إظهار كلمة المرور',
    hidePassword: 'إخفاء كلمة المرور',
    submit: 'تسجيل الدخول',
    usernameRequired: 'أدخل اسم المستخدم',
    passwordRequired: 'أدخل كلمة المرور',
    expired: 'انتهت جلستك. سجّل الدخول من جديد.',
    checking: 'جارٍ تسجيل دخولك',
    unreachableTitle: 'تعذّر الوصول إلى VLM',
    unreachableBody: 'الخادم لا يستجيب. تحقّق من الاتصال؛ تسجيل دخولك محفوظ.',
    newCompany: 'جديد على VLM؟',
    register: 'سجّل شركتك',
    scene: {
      lat: '21°25′ شمالاً',
      lng: '39°49′ شرقاً',
      scale: 'تكبير 14 • 1:34,000',
      from: 'من المستودع 3 • 08:02',
      eta: 'الوصول 10:15',
    },
  },

  roles: {
    ADMIN: 'مدير',
    OPERATOR: 'مشغّل',
    VIEWER: 'مشاهد',
  },

  roleHints: {
    ADMIN: 'كل شيء، بما في ذلك المستخدمون وإعدادات التنبيهات والفوترة.',
    OPERATOR: 'المركبات وأجهزة التتبع والربط والأسيجة الجغرافية والتنبيهات.',
    VIEWER: 'يطّلع على الخريطة المباشرة والرحلات والتنبيهات والتقارير.',
  },

  status: {
    ACTIVE: 'نشط',
    INACTIVE: 'غير نشط',
  },

  tracking: {
    MOVING: 'متحركة',
    STOPPED: 'متوقفة',
    OFFLINE: 'غير متصلة',
  },

  vehicleTypes: {
    CAR: 'سيارة',
    TRUCK: 'شاحنة',
    BUS: 'حافلة',
  },

  alertTypes: {
    OVERSPEED: 'تجاوز السرعة',
    GEOFENCE_ENTER: 'دخول سياج جغرافي',
    GEOFENCE_EXIT: 'خروج من سياج جغرافي',
    IDLE_TIME: 'خمول',
    HARSH_DRIVING: 'قيادة عنيفة',
  },

  alertStatus: {
    NEW: 'جديد',
    ACKNOWLEDGED: 'تم الاطلاع',
    RESOLVED: 'تم الحل',
  },

  geofenceTypes: {
    RADIUS: 'دائرة',
    POLYGON: 'مضلّع',
  },

  directions: {
    N: 'شمال',
    NE: 'شمال شرق',
    E: 'شرق',
    SE: 'جنوب شرق',
    S: 'جنوب',
    SW: 'جنوب غرب',
    W: 'غرب',
    NW: 'شمال غرب',
  },

  live: {
    title: 'الخريطة المباشرة',
    vehicles: 'المركبات',
    search: 'ابحث عن مركبة أو لوحة',
    noVehicles: 'لا مركبات على الخريطة بعد',
    noVehiclesBody: 'تظهر المركبة حين يُربط بها جهاز تتبع ويرسل موقعها.',
    noMatch: 'لا توجد مركبة مطابقة',
    noPosition: 'لا موقع بعد',
    lastReport: 'آخر إرسال',
    speed: 'السرعة',
    heading: 'الاتجاه',
    device: 'جهاز التتبع',
    position: 'الموقع',
    reported: 'آخر إرسال {ago}',
    trips: 'الرحلات',
    todaysTrack: 'مسار اليوم',
    follow: 'تتبّع',
    following: 'قيد التتبّع',
    zoomTo: 'التكبير على المركبة',
    closeDetails: 'إغلاق التفاصيل',
    fitAll: 'عرض الأسطول كاملًا',
    zoomIn: 'تكبير',
    zoomOut: 'تصغير',
    satellite: 'عرض القمر الصناعي',
    showGeofences: 'إظهار الأسيجة الجغرافية',
    hideGeofences: 'إخفاء الأسيجة الجغرافية',
    filter: 'التصفية حسب الحالة',
    newAlert: '{type} — {vehicle}',
  },

  mapSearch: {
    label: 'البحث في الخريطة',
    placeholder: 'ابحث عن مكان أو إحداثيات',
    coordinates: 'إحداثيات',
    searching: 'جارٍ البحث…',
    none: 'لم يُعثر على أماكن',
    failed: 'البحث عن الأماكن غير متاح الآن',
    found: {
      zero: 'لم يُعثر على أماكن',
      one: 'عُثر على مكان واحد',
      two: 'عُثر على مكانين',
      few: 'عُثر على {count} أماكن',
      many: 'عُثر على {count} مكانًا',
      other: 'عُثر على {count} مكان',
    },
  },

  vehicles: {
    title: 'المركبات',
    description: 'كل مركبات الأسطول وجهاز التتبع الذي تحمله كل منها.',
    add: 'إضافة مركبة',
    edit: 'تعديل المركبة',
    name: 'اسم المركبة',
    plate: 'رقم اللوحة',
    type: 'النوع',
    anyType: 'كل الأنواع',
    model: 'الطراز',
    year: 'سنة الصنع',
    color: 'اللون',
    device: 'جهاز التتبع',
    noDevice: 'بلا جهاز تتبع',
    search: 'ابحث بالاسم أو اللوحة',
    created: 'أُضيفت {name}',
    saved: 'حُفظت {name}',
    activateTitle: 'تفعيل {name}؟',
    activateBody: 'تعود إلى الخريطة المباشرة حين يرسل جهاز تتبعها موقعه.',
    deactivateTitle: 'تعطيل {name}؟',
    deactivateBody: 'تختفي من الخريطة المباشرة ولا تُطلق تنبيهات. تبقى رحلاتها وسجلها.',
    activated: '{name} نشطة الآن',
    deactivated: '{name} غير نشطة الآن',
    lastPosition: 'آخر موقع',
    lastPositionTitle: '{name} • آخر موقع',
    noPosition: 'لا موقع لهذه المركبة بعد',
    noPositionBody: 'يظهر حين يرسل جهاز التتبع المربوط بها موقعه.',
    empty: 'لا مركبات بعد',
    emptyBody: 'أضف أول مركبة، ثم اربط بها جهاز تتبع.',
    notFound: 'هذه المركبة غير موجودة',
  },

  devices: {
    title: 'أجهزة التتبع',
    description: 'الأجهزة داخل المركبات: رقم IMEI والشريحة والشبكة والمركبة التي فيها كل جهاز.',
    add: 'إضافة جهاز تتبع',
    edit: 'تعديل جهاز التتبع',
    name: 'اسم الجهاز',
    imei: 'IMEI',
    imeiLocked: 'رقم IMEI يعرّف الجهاز لدى البوابة؛ لا يمكن تغييره.',
    sim: 'رقم الشريحة',
    provider: 'الشبكة',
    anyProvider: 'كل الشبكات',
    otherProvider: 'أخرى',
    chooseProvider: 'اختر الشبكة',
    model: 'الطراز',
    vehicle: 'المركبة',
    notLinked: 'ليس في مركبة',
    search: 'ابحث بالاسم أو رقم IMEI',
    created: 'أُضيف {name}',
    saved: 'حُفظ {name}',
    activateTitle: 'تفعيل {name}؟',
    activateBody: 'تُحتسب إرسالاته من جديد.',
    deactivateTitle: 'تعطيل {name}؟',
    deactivateBody: 'لا تُحتسب إرسالاته بعد الآن. إن كان سيُستبدل، فألغِ ربطه بمركبته أولًا.',
    activated: '{name} نشط الآن',
    deactivated: '{name} غير نشط الآن',
    empty: 'لا أجهزة تتبع بعد',
    emptyBody: 'أضف جهازًا برقم IMEI الخاص به، ثم اربطه بمركبة.',
    notFound: 'جهاز التتبع هذا غير موجود',
  },

  assignments: {
    title: 'الربط',
    description: 'أي جهاز تتبع في أي مركبة. إلغاء الربط يوقف تتبع تلك المركبة.',
    link: 'ربط جهاز',
    linkTitle: 'ربط جهاز تتبع بمركبة',
    linkHint: 'يبدأ الربط من اليوم. تظهر فقط المركبات النشطة التي بلا جهاز، والأجهزة النشطة غير المركّبة في مركبة.',
    vehicle: 'المركبة',
    device: 'جهاز التتبع',
    chooseDevice: 'اختر جهاز تتبع',
    searchDevices: 'ابحث عن جهاز أو رقم IMEI',
    noDeviceMatch: 'لا يوجد جهاز مطابق',
    linkedOn: 'تاريخ الربط',
    noFreeVehicles: 'لكل مركبة نشطة جهاز تتبع بالفعل.',
    noFreeDevices: 'كل أجهزة التتبع النشطة مركّبة في مركبات بالفعل.',
    linked: '{device} الآن في {vehicle}',
    unlink: 'إلغاء الربط',
    unlinkTitle: 'إلغاء ربط {device}؟',
    unlinkBody: 'يتوقف تتبع {vehicle} حتى يُربط بها جهاز آخر.',
    unlinked: 'أُلغي ربط {device} بـ{vehicle}',
    linkedState: 'مربوط',
    unlinkedState: 'غير مربوط',
    search: 'ابحث برقم IMEI أو اللوحة',
    empty: 'لا عمليات ربط بعد',
    emptyBody: 'اربط جهاز تتبع بمركبة لتظهر على الخريطة.',
  },

  trips: {
    title: 'الرحلات',
    description: 'كل رحلة قطعها الأسطول: متى، وكم مسافة، وبأي سرعة.',
    trip: 'الرحلة',
    start: 'البداية',
    end: 'النهاية',
    duration: 'المدة',
    distance: 'المسافة',
    avgSpeed: 'متوسط السرعة',
    maxSpeed: 'أعلى سرعة',
    inProgress: 'جارية',
    inProgressHint: 'ما زالت المركبة تسير — لم تنتهِ هذه الرحلة بعد.',
    openTrip: 'فتح الرحلة {id}',
    empty: 'لا رحلات في هذه الفترة',
    emptyBody: 'تظهر الرحلات هنا لحظة بدء المركبة بالتحرك.',
  },

  trip: {
    title: 'الرحلة {id}',
    allTrips: 'كل الرحلات',
    route: 'المسار',
    timeline: 'التسلسل الزمني',
    started: 'بدأت الرحلة',
    ended: 'انتهت الرحلة',
    stillDriving: 'ما زالت تسير',
    stop: 'توقف',
    stopFor: 'توقفت لمدة {duration}',
    stopOngoing: 'متوقفة منذ {time}',
    stops: {
      zero: 'بلا توقفات',
      one: 'توقف واحد',
      two: 'توقفان',
      few: '{count} توقفات',
      many: '{count} توقفًا',
      other: '{count} توقف',
    },
    noRoute: 'لم تُسجَّل نقاط مسار لهذه الرحلة.',
    dayTrack: 'اليوم كاملًا على الخريطة',
    notFound: 'هذه الرحلة غير موجودة',
  },

  geofences: {
    title: 'الأسيجة الجغرافية',
    description: 'مناطق على الخريطة. دخول مركبة إليها أو خروجها منها يُطلق تنبيهًا.',
    add: 'رسم سياج جغرافي',
    edit: 'تعديل السياج الجغرافي',
    newTitle: 'سياج جغرافي جديد',
    name: 'الاسم',
    namePlaceholder: 'المستودع 3، سوق النور…',
    shape: 'الشكل',
    radius: 'نصف القطر',
    radiusUnit: 'متر',
    center: 'المركز',
    latitude: 'خط العرض',
    longitude: 'خط الطول',
    corners: 'الزوايا',
    cornerCount: {
      zero: 'لا زوايا بعد',
      one: 'زاوية واحدة',
      two: 'زاويتان',
      few: '{count} زوايا',
      many: '{count} زاوية',
      other: '{count} زاوية',
    },
    circleHint: 'ابحث عن مكان أو انقر على الخريطة لتحديد المركز، واسحبه لتحريكه.',
    polygonHint: 'انقر على الخريطة لإضافة الزوايا — 3 على الأقل. اسحب أي زاوية لتحريكها.',
    clearCorners: 'مسح الزوايا',
    removeCorner: 'إزالة الزاوية {n}',
    undoCorner: 'إزالة آخر زاوية',
    size: 'الحجم',
    created: 'حُفظ السياج الجغرافي {name}',
    saved: 'حُفظ السياج الجغرافي {name}',
    deleted: 'حُذف السياج الجغرافي {name}',
    deleteTitle: 'حذف {name}؟',
    deleteBody: 'إن دخلته مركبات أو خرجت منه، يبقى غير نشط ليُحفظ سجلها.',
    needCenter: 'حدّد المركز على الخريطة.',
    needRadius: 'اجعل نصف القطر أكبر من 0.',
    needCorners: 'أضف 3 زوايا على الأقل.',
    latRange: 'بين −90 و90',
    lngRange: 'بين −180 و180',
    map: 'الخريطة',
    createdOn: 'تاريخ الإنشاء',
    empty: 'لا أسيجة جغرافية بعد',
    emptyBody: 'ارسم منطقة — مستودعًا أو موقع عميل — لتعرف متى تصل المركبات ومتى تغادر.',
    notFound: 'هذا السياج الجغرافي غير موجود',
  },

  alerts: {
    title: 'التنبيهات',
    description: 'تجاوز السرعة والقيادة العنيفة والخمول وعبور الأسيجة الجغرافية في الأسطول كله.',
    alert: 'التنبيه {id}',
    type: 'النوع',
    anyType: 'كل الأنواع',
    time: 'الوقت',
    speed: 'السرعة',
    what: 'ما حدث',
    location: 'الموقع',
    noLocation: 'لم يُسجَّل موقع',
    acknowledge: 'تأكيد الاطلاع',
    resolve: 'تم الحل',
    acknowledged: 'تم الاطلاع على التنبيه {id}',
    resolved: 'حُلّ التنبيه {id}',
    nextStep: 'الخطوة التالية: {status}',
    done: 'تم الحل — لا شيء آخر مطلوب.',
    empty: 'لا تنبيهات في هذه الفترة',
    emptyBody: 'الطرق هادئة. تظهر التنبيهات هنا لحظة وقوعها.',
    notFound: 'هذا التنبيه غير موجود',
  },

  alertSettings: {
    title: 'إعدادات التنبيهات',
    description: 'أي التنبيهات يُطلقها VLM، وعند أي حد، ومن يتلقاها بالبريد الإلكتروني.',
    raise: 'إطلاق هذا التنبيه',
    speedLimit: 'حد السرعة',
    speedThreshold: 'تغيّر السرعة',
    idleTime: 'مدة الخمول',
    kmh: 'كم/س',
    kmhPerSecond: 'كم/س في الثانية',
    minutes: 'د',
    email: 'الإرسال بالبريد الإلكتروني',
    emailRecipients: 'عناوين البريد الإلكتروني',
    emailHint: 'افصل بين العناوين بفواصل، {max} عناوين على الأكثر. يصل بريد إلى كل عنوان منها مع كل تنبيه من هذا النوع.',
    needAddress: 'أدخل عنوان بريد إلكتروني واحدًا على الأقل.',
    badAddress: '{address} ليس عنوان بريد إلكتروني صالحًا.',
    maxAddresses: '{max} عناوين على الأكثر.',
    needThreshold: 'أدخل قيمة أكبر من 0.',
    cooldown: 'فترة الهدوء',
    needCooldown: 'أدخل عددًا صحيحًا من الدقائق، من 0 إلى 1440.',
    cooldownHint: {
      OVERSPEED: 'تنبيه واحد لكل نوبة تجاوز: لا تنبيه جديد إلا بعد أن تبقى المركبة تحت الحد هذه المدة. 0 = تنبيه مع كل إرسال فوق الحد.',
      HARSH_DRIVING: 'لا تنبيه جديد للمركبة نفسها خلال هذه المدة بعد آخر تنبيه. 0 = تنبيه مع كل كبح أو تسارع عنيف.',
    },
    saved: 'حُفظت إعدادات التنبيهات',
    unsaved: 'تغييرات غير محفوظة',
    discard: 'تجاهل التغييرات',
    about: {
      OVERSPEED: 'حين تتجاوز مركبة حد السرعة.',
      GEOFENCE_ENTER: 'حين تدخل مركبة سياجًا جغرافيًا.',
      GEOFENCE_EXIT: 'حين تخرج مركبة من سياج جغرافي.',
      IDLE_TIME: 'حين تقف مركبة ومحركها يعمل أطول من هذه المدة. يتطلّب جهاز تتبع يُبلغ عن حالة التشغيل.',
      HARSH_DRIVING: 'حين تتغيّر سرعة المركبة في ثانية واحدة بأكثر من هذا — كبح عنيف أو تسارع عنيف.',
    },
  },

  alertText: {
    OVERSPEED: 'تجاوزت المركبة {vehicle} حد السرعة: {speed} (الحد {limit}).',
    HARSH_DRIVING: 'تغيّرت سرعة المركبة {vehicle} بمقدار {change} — كبح عنيف أو تسارع عنيف.',
    GEOFENCE_ENTER: 'دخلت المركبة {vehicle} السياج الجغرافي {geofence}.',
    GEOFENCE_EXIT: 'خرجت المركبة {vehicle} من السياج الجغرافي {geofence}.',
    IDLE_TIME: 'المركبة {vehicle} متوقفة ومحركها يعمل منذ {duration} (الحد {limit}).',
  },

  users: {
    title: 'المستخدمون',
    description: 'من يمكنه من شركتك تسجيل الدخول إلى إدارة VLM، وما يمكن لكل منهم فعله.',
    add: 'إضافة مستخدم',
    edit: 'تعديل المستخدم',
    username: 'اسم المستخدم',
    usernameLocked: 'لا يمكن تغيير اسم المستخدم.',
    fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    password: 'كلمة المرور',
    passwordHint: '8 أحرف على الأقل.',
    passwordShort: '8 أحرف على الأقل',
    role: 'الدور',
    anyRole: 'كل الأدوار',
    language: 'اللغة',
    lastLogin: 'آخر تسجيل دخول',
    search: 'ابحث باسم المستخدم أو الاسم',
    you: 'أنت',
    created: 'أُضيف {name}',
    saved: 'حُفظ {name}',
    activateTitle: 'تفعيل {name}؟',
    activateBody: 'سيتمكن من تسجيل الدخول مجددًا.',
    deactivateTitle: 'تعطيل {name}؟',
    deactivateBody: 'لن يتمكن من تسجيل الدخول حتى يُفعَّل من جديد.',
    activated: '{name} نشط الآن',
    deactivated: '{name} غير نشط الآن',
    empty: 'لم يُعثر على مستخدمين',
    emptyBody: 'أضف الأشخاص الذين يديرون الأسطول.',
    notFound: 'هذا المستخدم غير موجود',
  },

  profile: {
    title: 'ملفك الشخصي',
    description: 'بياناتك وكلمة مرورك وطريقة ظهور VLM لك.',
    details: 'البيانات',
    account: 'الحساب',
    photo: 'الصورة',
    changePhoto: 'تغيير الصورة',
    uploadPhoto: 'رفع',
    removePhoto: 'إزالة الصورة',
    removePhotoTitle: 'إزالة صورتك؟',
    removePhotoBody: 'ستظهر الأحرف الأولى من اسمك بدلًا منها.',
    photoRules: 'JPEG أو PNG أو WebP، حتى 5 ميغابايت.',
    photoType: 'هذا الملف ليس صورة JPEG أو PNG أو WebP.',
    photoSize: 'حجم هذه الصورة أكبر من 5 ميغابايت.',
    photoUpdated: 'حُدّثت الصورة',
    photoRemoved: 'أُزيلت الصورة',
    saved: 'حُفظ الملف الشخصي',
    password: 'كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'أعد كتابة كلمة المرور الجديدة',
    mismatch: 'كلمتا المرور غير متطابقتين.',
    changePassword: 'تغيير كلمة المرور',
    passwordChanged: 'تغيّرت كلمة المرور',
    preferences: 'التفضيلات',
    preferencesHint: 'تُحفظ في هذا المتصفح؛ وتُحفظ اللغة في حسابك أيضًا.',
  },

  reports: {
    title: 'التقارير',
    description: 'الرحلات والنشاط والتنبيهات وزيارات الأسيجة الجغرافية لأي فترة — على الشاشة، أو بصيغة CSV أو Excel أو PDF.',
    open: 'فتح',
    run: 'إنشاء التقرير',
    export: 'تصدير',
    exportAs: 'تصدير بصيغة {format}',
    exporting: 'جارٍ تجهيز ملف {format}',
    exported: 'نُزّل {file}',
    exportFailed: 'فشل التصدير. حاول مرة أخرى.',
    needRange: 'اختر فترة.',
    badRange: 'يجب أن تكون النهاية بعد البداية.',
    needVehicle: 'اختر مركبة.',
    rows: {
      zero: 'لا صفوف',
      one: 'صف واحد',
      two: 'صفّان',
      few: '{count} صفوف',
      many: '{count} صفًا',
      other: '{count} صف',
    },
    emptyTitle: 'لا شيء في هذه الفترة',
    emptyBody: 'جرّب فترة أطول أو مركبة أخرى.',
    prompt: 'اختر فترة، ثم أنشئ التقرير.',
    total: 'الإجمالي',
    cards: {
      trips: { title: 'الرحلات', body: 'كل رحلة منتهية بمسافتها ومدتها ومتوسط سرعتها.' },
      activity: { title: 'نشاط المركبات', body: 'لكل مركبة: المسافة والرحلات ووقت القيادة والخمول والتوقف وأعلى سرعة.' },
      track: { title: 'مستكشف المسار', body: 'كل موقع أرسلته مركبة خلال فترة — على الخريطة وفي جدول.' },
      alerts: { title: 'التنبيهات', body: 'سجلات التنبيهات، مع توزيعها حسب المركبة وحسب النوع.' },
      geofences: { title: 'زيارات الأسيجة الجغرافية', body: 'متى دخلت المركبات الأسيجة الجغرافية وخرجت منها، وكم بقيت فيها.' },
    },
    tripCount: 'الرحلات',
    totalDistance: 'المسافة',
    drivingTime: 'وقت القيادة',
    averageSpeed: 'متوسط السرعة',
    activity: {
      distance: 'المسافة',
      trips: 'الرحلات',
      driving: 'القيادة',
      idling: 'الخمول',
      stopped: 'التوقف',
      topSpeed: 'أعلى سرعة',
    },
    track: {
      positions: {
        zero: 'لا نقاط',
        one: 'نقطة واحدة',
        two: 'نقطتان',
        few: '{count} نقاط',
        many: '{count} نقطة',
        other: '{count} نقطة',
      },
      positionsLabel: 'النقاط',
      noPositions: 'لا مواقع في هذه الفترة',
      noPositionsBody: 'لم ترسل المركبة موقعها بين هذين الوقتين. جرّب فترة أوسع.',
      mapTab: 'الخريطة',
      tableTab: 'الجدول',
      start: 'البداية',
      end: 'النهاية',
      time: 'الوقت',
      latitude: 'خط العرض',
      longitude: 'خط الطول',
      speed: 'السرعة',
      heading: 'الاتجاه',
      distance: 'المسافة المقطوعة',
      topSpeed: 'أعلى سرعة',
      legend: 'السرعة',
      bandStopped: 'متوقفة أو غير معروفة',
      bandSlow: 'أقل من 60',
      bandMid: 'من 60 إلى 80',
      bandFast: 'من 80 إلى 120',
      bandTooFast: '120 فأكثر',
      showPoint: 'عرض النقطة {n} على الخريطة',
    },
    alerts: {
      perVehicle: 'التنبيهات لكل مركبة',
      byType: 'التنبيهات حسب النوع',
      records: 'سجلات التنبيهات',
      count: 'التنبيهات',
      locationTitle: 'موقع التنبيه',
    },
    geofences: {
      geofence: 'السياج الجغرافي',
      allGeofences: 'كل الأسيجة الجغرافية',
      entered: 'الدخول',
      left: 'الخروج',
      inside: 'مدة البقاء',
      stillInside: 'ما زالت داخله',
    },
  },

  errors: {
    network: 'تعذّر الوصول إلى VLM. تحقّق من الاتصال وحاول مرة أخرى.',
    server: 'تعذّر على VLM تنفيذ ذلك الآن ({status}). حاول مرة أخرى.',
    unknown: 'حدث خطأ ما. حاول مرة أخرى.',
    accessDenied: 'دورك لا يسمح بهذا.',
    notFound: 'لم يعد موجودًا — ربما حذفه أحدهم.',
    invalidCredentials: 'اسم المستخدم وكلمة المرور غير متطابقين.',
    deactivated: 'هذا الحساب معطّل. اطلب من أحد المديرين تفعيله.',
    lastAdmin: 'لا يمكنك تعطيل آخر مدير نشط.',
    wrongPassword: 'كلمة المرور الحالية غير صحيحة.',
    vehicleHasDevice: 'لهذه المركبة جهاز تتبع بالفعل. ألغِ ربطه أولًا.',
    deviceInUse: 'جهاز التتبع هذا في مركبة أخرى بالفعل. ألغِ ربطه أولًا.',
    duplicatePlate: 'توجد مركبة باللوحة {value} بالفعل.',
    duplicateImei: 'يوجد جهاز تتبع برقم IMEI {value} بالفعل.',
    duplicateUsername: 'اسم المستخدم {value} مستخدم بالفعل.',
    polygonCorners: 'يحتاج المضلّع إلى 3 زوايا على الأقل.',
    circleCenter: 'تحتاج الدائرة إلى مركز.',
    circleRadius: 'يجب أن يكون نصف القطر أكبر من 0.',
    companyPending: 'تسجيل شركتك بانتظار موافقة فيجانيوم.',
    companySuspended: 'حساب شركتك موقوف. تواصل مع فيجانيوم لإعادته.',
    companyRejected: 'لم تتم الموافقة على تسجيل شركتك. تواصل مع فيجانيوم لمعرفة التفاصيل.',
    companyInactive: 'حساب شركتك غير فعّال. تواصل مع فيجانيوم.',
    companyTrialEnded: 'انتهت الفترة التجريبية المجانية لشركتك. تواصل مع فيجانيوم لاختيار باقة — بياناتك محفوظة.',
    planVehicles: {
      one: 'تسمح باقتك بمركبة نشطة واحدة. عطّلها أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      two: 'تسمح باقتك بمركبتين نشطتين على الأكثر. عطّل إحداهما أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      few: 'تسمح باقتك بـ {count} مركبات نشطة على الأكثر. عطّل إحداها أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      many: 'تسمح باقتك بـ {count} مركبة نشطة على الأكثر. عطّل إحداها أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      other: 'تسمح باقتك بـ {count} مركبة نشطة على الأكثر. عطّل إحداها أولًا، أو اطلب من فيجانيوم باقة أكبر.',
    },
    planUsers: {
      one: 'تسمح باقتك بمستخدم نشط واحد. عطّله أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      two: 'تسمح باقتك بمستخدمَين نشطَين على الأكثر. عطّل أحدهما أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      few: 'تسمح باقتك بـ {count} مستخدمين نشطين على الأكثر. عطّل أحدهم أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      many: 'تسمح باقتك بـ {count} مستخدمًا نشطًا على الأكثر. عطّل أحدهم أولًا، أو اطلب من فيجانيوم باقة أكبر.',
      other: 'تسمح باقتك بـ {count} مستخدم نشط على الأكثر. عطّل أحدهم أولًا، أو اطلب من فيجانيوم باقة أكبر.',
    },
    validation: 'بعض الحقول تحتاج إلى مراجعة.',
  },

  validation: {
    field: '{field}: {problem}',
    NotNull: 'مطلوب',
    NotBlank: 'مطلوب',
    NotEmpty: 'مطلوب',
    Size: 'من {min} إلى {max} حرفًا',
    SizeMax: '{max} حرفًا على الأكثر',
    SizeMin: '{min} على الأقل',
    Email: 'عنوان بريد إلكتروني غير صالح',
    Pattern: 'ليس بالصيغة المتوقعة',
    Min: '{value} على الأقل',
    Max: '{value} على الأكثر',
    Positive: 'يجب أن يكون أكبر من 0',
    invalid: 'غير مقبول',
  },

  billing: {
    title: 'الفوترة',
    description: 'باقتك والدفعة القادمة وفواتيرك.',
    plan: 'الباقة',
    noPlan: 'لا باقة بعد',
    noPlanBody: 'تحدّد فيجانيوم باقتك. تواصل معنا لاختيار واحدة.',
    trial: 'الفترة التجريبية المجانية',
    trialLeft: { zero: 'انتهت', one: 'آخر يوم', two: 'بقي يومان', few: 'بقيت {count} أيام', many: 'بقي {count} يومًا', other: 'بقي {count} يوم' },
    trialUntil: 'حتى نهاية يوم {date}',
    trialBody: 'كل شيء بلا حدود خلال فترتك التجريبية المجانية: أي عدد من المركبات والمستخدمين.',
    trialNoPlan: 'اختر باقة مع فيجانيوم قبل انتهائها، فبعدها لا يستطيع أحد في شركتك تسجيل الدخول حتى تكون لها باقة. مركباتك وسجلّها محفوظة.',
    trialThenPlan: 'تبدأ باقتك في اليوم التالي لانتهائها.',
    price: { MONTHLY: '{price} شهريًا', YEARLY: '{price} سنويًا' },
    beforeVat: 'قبل الضريبة • تُضاف {rate}% في الفاتورة',
    paidUntil: 'مدفوعة حتى',
    notPaid: 'لم تُدفع بعد',
    usage: 'استخدام الباقة',
    vehicles: 'المركبات النشطة',
    users: 'المستخدمون النشطون',
    ofLimit: '{used} من {limit}',
    unlimited: '{used} • بلا حد',
    next: 'الدفعة القادمة',
    dueOn: 'تستحق في {date}',
    overdueSince: 'متأخرة منذ {date}',
    invoice: 'الفاتورة {number}',
    expected: 'لم تصدر فاتورتها بعد: هذا ما ستكون عليه الفاتورة القادمة.',
    nothingDue: 'لا يوجد ما يُدفع الآن.',
    transfer: 'الدفع بتحويل بنكي',
    bank: 'البنك',
    accountName: 'اسم الحساب',
    iban: 'رقم الآيبان',
    swift: 'رمز السويفت',
    reference: 'المرجع',
    referenceHint: 'اكتب رمز شركتك مرجعًا للتحويل، ليُنسب الدفع إلى شركتك.',
    afterTransfer: 'تفعّل فيجانيوم باقتك أو تمدّدها فور وصول التحويل.',
    receipt: 'أرسل إيصال التحويل إلى {email}.',
    noBank: 'اطلب تفاصيل الحساب البنكي من فيجانيوم.',
    copy: 'نسخ {label}',
    copied: 'نُسخ {label}',
    invoices: 'الفواتير',
    invoicesEmpty: 'لا فواتير بعد',
    invoicesEmptyBody: 'تظهر فواتيرك هنا فور إصدار فيجانيوم لها.',
    number: 'الفاتورة',
    period: 'الفترة',
    issued: 'تاريخ الإصدار',
    due: 'الاستحقاق',
    total: 'الإجمالي',
    download: 'تنزيل {number}',
    pdfEn: 'PDF بالإنجليزية',
    pdfAr: 'PDF بالعربية',
    downloaded: 'نُزّل {file}',
    downloadFailed: 'تعذّر تنزيل الفاتورة. حاول مرة أخرى.',
    details: 'بيانات الفوترة',
    detailsHint: 'ما تذكره فواتيرك القادمة عن شركتك.',
    edit: 'تعديل بيانات الفوترة',
    name: 'اسم الشركة',
    nameHint: 'لتغيير اسم الشركة، تواصل مع فيجانيوم.',
    code: 'رمز الشركة',
    contactName: 'الشخص المسؤول',
    email: 'بريد الفوترة',
    emailHint: 'تُرسَل الفواتير والإيصالات وتذكيرات الدفع إلى هذا البريد.',
    emailHintNone: 'تُرسَل الفواتير والإيصالات وتذكيرات الدفع إلى مديري شركتك إلى أن تضيف بريدًا للفوترة.',
    phone: 'الهاتف',
    address: 'العنوان',
    city: 'المدينة',
    country: 'الدولة',
    taxNumber: 'الرقم الضريبي',
    commercialRegistration: 'السجل التجاري',
    invoiceLanguage: 'لغة الفاتورة',
    notSet: 'غير محدد',
    saved: 'حُفظت بيانات الفوترة',
  },

  subscriptionStatus: {
    NONE: 'بلا باقة',
    PENDING: 'بانتظار الدفع',
    ACTIVE: 'مدفوع',
    PAST_DUE: 'متأخر',
    CANCELED: 'ملغى',
  },

  invoiceStatus: {
    UNPAID: 'غير مدفوعة',
    PAID: 'مدفوعة',
    VOID: 'ملغاة',
    OVERDUE: 'متأخرة',
  },

  cycles: {
    MONTHLY: 'شهري',
    YEARLY: 'سنوي',
  },

  pages: {
    notFoundTitle: 'هذه الصفحة ليست على الخريطة',
    notFoundBody: 'ربما يكون العنوان قديمًا، أو نُقلت الصفحة.',
    goLive: 'الانتقال إلى الخريطة المباشرة',
    forbiddenTitle: 'لا صلاحية',
    forbiddenBody: 'دورك لا يشمل هذه الصفحة. اطلب من أحد المديرين إن كنت تحتاجها.',
  },
};
